import { useId } from "react";
import { CONSIGNMENT_ARRANGEMENTS, CONSIGNMENT_BASES } from "@ondo/shared";

import { RevisionsPanel } from "../revisions/RevisionsPanel.tsx";
import { CONSIGN_COPY } from "./consign.constants.ts";
import type { ConsignmentSummary } from "./consign.model.ts";
import { useConsignController } from "./useConsignController.ts";
import type { ConsignController } from "./useConsignController.ts";
import "./consign.css";

/**
 * A work's consignment: who holds it, on what terms, and the two acts.
 *
 * Mounted beside the agents panel on a work's record, because that is where
 * the gallery's own copy says the act lives — "an artist or estate consigns a
 * work from its own record" — and on the agent's Share Codes screen under
 * "Agree a consignment". One component; the workId is the only thing that
 * differs.
 *
 * `canAct` draws the controls. It is the account-level `consignWork`
 * capability, which is the server's answer about whether this account may
 * consign ANYTHING — the same hint `canAssignAgent` is. Whether it may
 * consign THIS work is `ondo.offer_consignment()`'s to refuse, and it does.
 */
export function ConsignPanel({ workId, canAct }: { workId: string; canAct: boolean }) {
  const c = useConsignController(workId, canAct);
  const { view } = c;

  return (
    <section className="consign">
      <header className="consign__head">
        <h2 className="consign__title">{CONSIGN_COPY.title}</h2>
        {view.kind === "none" && canAct && !c.form.open && (
          <button type="button" className="consign__action" onClick={c.openForm}>
            {CONSIGN_COPY.offer.open}
          </button>
        )}
      </header>

      {view.kind === "loading" && <p className="consign__note">{view.message}</p>}

      {view.kind === "none" && !c.form.open && (
        <>
          <p className="consign__none">{view.message}</p>
          {view.detail && <p className="consign__note">{view.detail}</p>}
          {c.form.sent && (
            <p className="consign__sent" role="status">
              {CONSIGN_COPY.offer.sent}
            </p>
          )}
        </>
      )}

      {view.kind === "none" && c.form.open && <OfferForm c={c} />}

      {view.kind === "held" && (
        <>
          <p className="consign__headline">{view.summary.headline}</p>
          <dl className="consign__terms">
            {view.summary.terms.map((term) => (
              <div key={term.label}>
                <dt>{term.label}</dt>
                <dd>{term.value}</dd>
              </div>
            ))}
          </dl>
          {/* The terms panel, and only once the gallery has accepted — which is
              the set `ondo.propose_consignment_revision()` admits. The terms of
              an unanswered OFFER are changed by withdrawing it and offering
              again, and the control for that is below. */}
          {view.summary.accepted && (
            <RevisionsPanel consignmentId={view.summary.consignmentId} canAct={canAct} />
          )}
          {/* Where the work is, and whether the gallery may know — OND-88. The
              owner's switch, beside the arrangement it is about, and only for a
              reader who may act: a seat that cannot change it sees no box and
              no sentence, because a disclosure setting is the owner's to read
              as much as to set. */}
          {canAct && <LocationSharingControl c={c} summary={view.summary} />}
          {canAct && <EndControl c={c} accepted={view.summary.accepted} />}
        </>
      )}

      {c.error && (
        <p className="consign__note consign__note--error" role="alert">
          {c.error}
        </p>
      )}
    </section>
  );
}

function OfferForm({ c }: { c: ConsignController }) {
  const { draft, check, problem, sending } = c.form;
  const copy = CONSIGN_COPY.offer;
  const idId = useId();
  const arrId = useId();
  const basisId = useId();
  const curId = useId();
  const askId = useId();
  const floorId = useId();
  const noteId = useId();

  return (
    <form
      className="consign__form"
      onSubmit={(event) => {
        event.preventDefault();
        c.offer();
      }}
    >
      <h3 className="consign__form-title">{copy.title}</h3>

      <label className="consign__label" htmlFor={idId}>
        {copy.gallery}
      </label>
      <div>
        <input
          id={idId}
          className="consign__input consign__input--id"
          value={draft.ondoId}
          onChange={(event) => c.setDraft({ ondoId: event.target.value })}
          onBlur={c.checkGallery}
          placeholder={copy.galleryPlaceholder}
          autoComplete="off"
          spellCheck={false}
          disabled={sending}
          required
        />
        {/* The server's verdict, in the server's words — never a sentence
            written here, so the blur and the submit cannot disagree. */}
        {check?.state === "checking" && <p className="consign__hint">{copy.checking}</p>}
        {check?.state === "eligible" && (
          <p className="consign__hint consign__hint--ok">{copy.eligible(check.displayName)}</p>
        )}
        {check?.state === "refused" && (
          <p className="consign__hint consign__hint--refused">{check.message}</p>
        )}
        {!check && <p className="consign__hint">{copy.galleryHelp}</p>}
      </div>

      <label className="consign__label" htmlFor={arrId}>
        {CONSIGN_COPY.terms.arrangement}
      </label>
      <div>
        <select
          id={arrId}
          className="consign__input"
          value={draft.arrangement}
          onChange={(event) =>
            c.setDraft({ arrangement: event.target.value as typeof draft.arrangement })
          }
          disabled={sending}
        >
          {CONSIGNMENT_ARRANGEMENTS.map((each) => (
            <option key={each} value={each}>
              {CONSIGN_COPY.arrangement[each]}
            </option>
          ))}
        </select>
        <p className="consign__hint">{CONSIGN_COPY.arrangementHelp[draft.arrangement]}</p>
      </div>

      <label className="consign__label" htmlFor={basisId}>
        {CONSIGN_COPY.terms.basis}
      </label>
      <select
        id={basisId}
        className="consign__input"
        value={draft.basis}
        onChange={(event) => c.setDraft({ basis: event.target.value as typeof draft.basis })}
        disabled={sending}
      >
        {CONSIGNMENT_BASES.map((each) => (
          <option key={each} value={each}>
            {CONSIGN_COPY.basis[each]}
          </option>
        ))}
      </select>

      <label className="consign__label" htmlFor={curId}>
        {copy.currency}
      </label>
      <input
        id={curId}
        className="consign__input consign__input--currency"
        value={draft.currency}
        onChange={(event) => c.setDraft({ currency: event.target.value })}
        maxLength={3}
        disabled={sending}
      />

      <label className="consign__label" htmlFor={askId}>
        {copy.asking}
      </label>
      <div>
        <input
          id={askId}
          className="consign__input consign__input--money"
          value={draft.asking}
          onChange={(event) => c.setDraft({ asking: event.target.value })}
          inputMode="decimal"
          disabled={sending}
        />
        <p className="consign__hint">{copy.askingHelp}</p>
      </div>

      <label className="consign__label" htmlFor={floorId}>
        {copy.floor}
      </label>
      <div>
        <input
          id={floorId}
          className="consign__input consign__input--money"
          value={draft.floor}
          onChange={(event) => c.setDraft({ floor: event.target.value })}
          inputMode="decimal"
          disabled={sending}
        />
        <p className="consign__hint">{copy.floorHelp}</p>
      </div>

      <label className="consign__label" htmlFor={noteId}>
        {copy.note}
      </label>
      <textarea
        id={noteId}
        className="consign__input consign__input--note"
        value={draft.note}
        onChange={(event) => c.setDraft({ note: event.target.value })}
        rows={3}
        maxLength={2000}
        disabled={sending}
      />

      {problem && (
        <p className="consign__problem" role="alert">
          {problem}
        </p>
      )}

      <div className="consign__form-actions">
        <button type="submit" className="consign__submit" disabled={sending}>
          {sending ? copy.sending : copy.submit}
        </button>
        <button type="button" className="consign__cancel" onClick={c.closeForm} disabled={sending}>
          {copy.cancel}
        </button>
      </div>
    </form>
  );
}

/**
 * Two presses, the shape ending a sale takes. Before acceptance it is a
 * withdrawal and after it a recall, and the sentence between the presses
 * says which — and that recalling stands down any agent the gallery appointed.
 */
function LocationSharingControl({
  c,
  summary,
}: {
  c: ConsignController;
  summary: ConsignmentSummary;
}) {
  const copy = CONSIGN_COPY.location;
  const id = useId();

  return (
    <div className="consign__location">
      <label className="consign__location-label" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          className="consign__location-input"
          checked={summary.location.shared}
          disabled={c.savingLocationSharing}
          onChange={(event) => c.setLocationSharing(event.target.checked)}
        />
        {copy.label}
      </label>
      <p className="consign__note">{c.savingLocationSharing ? copy.saving : summary.location.note}</p>
    </div>
  );
}

function EndControl({ c, accepted }: { c: ConsignController; accepted: boolean }) {
  const copy = CONSIGN_COPY.recall;

  if (!c.confirmingEnd) {
    return (
      <button type="button" className="consign__end" disabled={c.ending} onClick={c.askToEnd}>
        {accepted ? copy.recall : copy.withdraw}
      </button>
    );
  }

  return (
    <div className="consign__end-confirm">
      <p className="consign__end-note">{accepted ? copy.recallNote : copy.withdrawNote}</p>
      <div className="consign__form-actions">
        <button type="button" className="consign__end-yes" disabled={c.ending} onClick={c.end}>
          {accepted ? copy.confirmRecall : copy.confirmWithdraw}
        </button>
        <button type="button" className="consign__cancel" onClick={c.cancelEnd} disabled={c.ending}>
          {copy.cancel}
        </button>
      </div>
    </div>
  );
}
